"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { AnimatePresence, motion } from "framer-motion"
import { Plus, ChevronRight, ChevronLeft } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import Tooltip from "@mui/material/Tooltip"
import { SlidesIcon, PitchDeckIcon, CapTableIcon, DocumentsIcon, ResearchIcon } from "./home/deck-type-icons"
import { useAppSelector } from "@/app/store/hooks"
import { trackEvent } from "@/lib/mixpanel"
import dynamic from "next/dynamic"

const LoginPopover = dynamic(
    () => import("../auth/login-popover").then((mod) => mod.LoginPopover),
    { ssr: false }
)

const DOCUMENT_SUBTYPES = [
    { label: "Custom", value: "custom"},
    { label: "Partnership Proposal", value: "partnership-proposal" },
    { label: "NDA", value: "NDA" },
    { label: "Resume", value: "resume" },
    { label: "Business Document", value: "business-document" },
    { label: "Service Agreement", value: "service-agreement" },
]

interface NewDeckMenuProps {
    isCollapsed?: boolean
}

export function NewDeckMenu({ isCollapsed = false }: NewDeckMenuProps) {
    const router = useRouter()
    const { token, isAuthenticated } = useAppSelector((state) => state.auth)
    const isLoggedIn = !!(token && isAuthenticated)
     const [isOpen, setIsOpen] = useState(false)
     const [isLoginPopoverOpen, setIsLoginPopoverOpen] = useState(false)
     // "main" shows deck types, "documents" shows document subtypes
     const [view, setView] = useState<"main" | "documents">("main")

    const close = () => {
        setIsOpen(false)
        // Reset view after close animation
        setTimeout(() => setView("main"), 200)
    }

    const handleCreateSlides = () => {
        trackEvent("New Deck Clicked", { deck_type: "Presentation" })
        close()
        router.push("/?type=Presentation")
    }

    const handleNavigate = (type: string, subtype?: string) => {
        trackEvent("New Deck Clicked", { deck_type: type, deck_subtype: subtype })
        close()
        const url = subtype
            ? `/?type=${type}&subtype=${subtype}`
            : `/?type=${type}`
        router.push(url)
    }

    const menuItems = [
        {
            id: "Presentation",
            label: "Presentation",
            subtitle: "From scratch",
            icon: SlidesIcon,
            iconColor: "text-blue-600",
            bgColor: "from-blue-400/20 to-blue-600/20",
            action: handleCreateSlides,
        },
        {
            id: "fundraising-pitch-deck",
            label: "Pitch Deck",
            subtitle: "Investor ready",
            icon: PitchDeckIcon,
            iconColor: "text-amber-600",
            bgColor: "from-amber-400/20 to-amber-600/20",
            action: () => handleNavigate("fundraising-pitch-deck"),
        },
        {
            id: "documents",
            label: "Documents",
            subtitle: "Legal docs & proposals",
            icon: DocumentsIcon,
            iconColor: "text-indigo-600",
            bgColor: "from-indigo-400/20 to-indigo-600/20",
            action: () => setView("documents"),
        },
        {
            id: "deep-research",
            label: "Deep Research",
            subtitle: "Multi-source research & analysis",
            icon: ResearchIcon,
            iconColor: "text-rose-600",
            bgColor: "from-rose-400/20 to-rose-600/20",
            action: () => handleNavigate("deep-research"),
        },
    ]

    return (
        <Popover
            open={isOpen}
            onOpenChange={(open) => {
                if (open && !isLoggedIn) {
                    setIsLoginPopoverOpen(true)
                    return
                }
                setIsOpen(open)
                if (!open) setTimeout(() => setView("main"), 200)
            }}
        >
            <PopoverTrigger asChild>
                {isCollapsed ? (
                    <Tooltip title="New Deck" placement="right" arrow>
                        <button
                            className={cn(
                                "flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg transition-all duration-200",
                                "text-slate-400 hover:text-blue-600 hover:bg-blue-50 active:scale-95"
                            )}
                        >
                            <Plus className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                    </Tooltip>
                ) : (
                    <button
                        className={cn(
                            "relative group cursor-pointer w-full flex items-center gap-3 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-all duration-200 whitespace-nowrap overflow-hidden",
                            "text-slate-700 hover:text-slate-900"
                        )}
                    >
                        <Plus className="h-3.5 w-3.5 flex-shrink-0 text-slate-700 group-hover:text-slate-900" strokeWidth={2.5} />
                        <span className="truncate">New Deck</span>
                    </button>
                )}
            </PopoverTrigger>

            <PopoverContent
                className="w-72 p-0 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden"
                sideOffset={8}
                side="right"
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <AnimatePresence mode="wait" initial={false}>
                    {view === "main" ? (
                        <motion.div
                            key="main"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.15, ease: "easeOut" }}
                        >
                            {/* Header */}
                            <div className="px-5 py-4 border-b border-slate-100">
                                <h3 className="text-sm font-semibold text-slate-900">Create New</h3>
                                <p className="text-xs text-slate-500 mt-0.5">Choose a type to get started</p>
                            </div>

                            {/* Items */}
                            <div className="p-2">
                                {menuItems.map((item, index) => {
                                    const Icon = item.icon
                                    const isDocuments = item.id === "documents"

                                    return (
                                        <motion.button
                                            key={item.id}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.04 }}
                                            onClick={item.action}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left group cursor-pointer"
                                        >
                                            <div className={cn(
                                                "flex h-8 w-8 items-center justify-center rounded-md flex-shrink-0 transition-all duration-200 bg-gradient-to-br",
                                                item.bgColor, "group-hover:shadow-md"
                                            )}>
                                                <Icon className={cn("h-4 w-4", item.iconColor)} strokeWidth={1.8} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-900">{item.label}</p>
                                                <p className="text-xs text-slate-500">{item.subtitle}</p>
                                            </div>
                                            {isDocuments && (
                                                <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
                                            )}
                                        </motion.button>
                                    )
                                })}
                            </div>

                            {/* Footer */}
                            {/* <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50">
                                <p className="text-xs text-slate-500">Tip: Use templates to get started faster</p>
                            </div> */}
                        </motion.div>
                    ) : (
                        <motion.div
                            key="documents"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.15, ease: "easeOut" }}
                        >
                            {/* Header with back button */}
                            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                                <button
                                    onClick={() => setView("main")}
                                    className="p-1 -ml-1 rounded-md hover:bg-slate-100 transition-colors cursor-pointer"
                                >
                                    <ChevronLeft className="h-4 w-4 text-slate-500" />
                                </button>
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-900">Documents</h3>
                                    <p className="text-xs text-slate-500">Choose a document type</p>
                                </div>
                            </div>

                            {/* Document subtypes */}
                            <div className="p-2">
                                {DOCUMENT_SUBTYPES.map((docType, index) => (
                                    <motion.button
                                        key={docType.value}
                                        initial={{ opacity: 0, x: 10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.03 }}
                                        onClick={() => handleNavigate("documents", docType.value)}
                                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-blue-50 hover:text-blue-700 transition-colors text-left text-[13px] text-slate-600 cursor-pointer"
                                    >
                                        <DocumentsIcon className="h-4 w-4 text-indigo-500 flex-shrink-0" strokeWidth={1.8} />
                                        {docType.label}
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </PopoverContent>
            <LoginPopover
                isOpen={isLoginPopoverOpen}
                onClose={() => setIsLoginPopoverOpen(false)}
            />
        </Popover>
    )
}
